import React from 'react';

export interface ErrorBoundaryProps {
  /** Rendered instead of the children when they throw. */
  fallback: React.ReactNode;
  children: React.ReactNode;
}

interface ErrorBoundaryState {
  failed: boolean;
}

/**
 * Contains a render failure to one part of the page.
 *
 * React unmounts the WHOLE tree when a render throws and nothing catches it, so
 * without one of these a single bad node blanks the entire site — header, nav
 * and all — leaving a white page with no way back. That is the difference
 * between "this one section could not be shown" and "the website is broken".
 *
 * Needed here in particular because the meaning renderer walks editor-authored
 * content and loads its parser from a separate chunk: a malformed document, or
 * a chunk request that fails on a flaky connection (a deploy mid-session
 * invalidates the old chunk URLs), both surface as a throw during render.
 *
 * Class component because that is the only way to catch: there is no hook
 * equivalent of getDerivedStateFromError.
 */
export class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  state: ErrorBoundaryState = { failed: false };

  static getDerivedStateFromError(): ErrorBoundaryState {
    return { failed: true };
  }

  componentDidCatch(error: unknown) {
    // Console only. There is no error-reporting service wired up in this app,
    // and swallowing it silently would make the fallback impossible to debug.
    // eslint-disable-next-line no-console
    console.error('ErrorBoundary caught:', error);
  }

  render() {
    return this.state.failed ? this.props.fallback : this.props.children;
  }
}

export default ErrorBoundary;
